<template>
    <v-container class="edit-campaign-devices-and-services">
      <v-snackbar
        v-model="snackbar.show"
        :timeout="snackbar.timeout"
        top
        :color="snackbar.color"
        rounded
        centered
      >
        {{ snackbar.text }}
      </v-snackbar>



      <!-- Title and subtitle -->
      <div class="campaign-header-container">
        <h3 class="header-main py-3"><b>{{$t('OrganizationData.dashboard')}}</b> {{organization}}</h3>
        <div class="campaign-selector-container">
          <div v-if="!isEditingCampaignName" class="campaign-name-display">
            <p class="body-text">
              <b>{{$t('EditCampaign.service')}}</b>
              {{selectedCampaign ? selectedCampaign.name : ''}}
            </p>
            <v-btn class="ml-2" text fab x-small @click="startEditingCampaignName" v-if="selectedCampaign">
              <v-icon color="#00202F" size="16">mdi-pencil</v-icon>
            </v-btn>
          </div>
          <div v-else class="campaign-name-edit">
            <v-text-field
              v-model="editedCampaignName"
              outlined
              dense
              hide-details
              class="campaign-name-input"
              @blur="handleBlur"
              @keyup.enter="saveCampaignName"
              @keyup.esc="cancelEditingCampaignName"
              :disabled="loadingCampaignName"
              ref="campaignNameInput"
            />
            <v-btn :loading="loadingCampaignName" class="ml-2" text fab x-small @click="saveCampaignName" color="success">
              <v-icon size="16">mdi-check</v-icon>
            </v-btn>
            <v-btn class="ml-1" text fab x-small @mousedown="cancelEditingCampaignName" color="error">
              <v-icon size="16">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <v-divider class="mb-4"></v-divider>

      <div class="text-center mt-4">
        <v-progress-circular
          v-if="isLoading"
          indeterminate
          color="#0098E1"
        />
      </div>

      <v-alert
        v-if="!isLoading && products.length === 0"
        type="info"
        class="mt-4"
      >
        {{ $t('EditCampaign.noProductsAvailable') }}
      </v-alert>

      <v-form v-if="!isLoading && products.length > 0" v-model="isFormValid" ref="devicesForm" class="action-row">
        <div>
          <!-- Monitoring products -->
          <template v-for="(product, index) in monitoringProducts">
            <v-row align="baseline">
              <p class="body-text pointer" @click="toggleProductExpanded('monitoring', index)"><b>{{product.quantity}}</b> {{getProductDetailText(product)}}</p>
              <v-btn class="ml-2" text fab x-small @click="toggleProductExpanded('monitoring', index)">
                <v-icon
                  color="black"
                >{{monitoringProductsExpanded[index] ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon>
              </v-btn>
            </v-row>
            <div v-for="(deviceInfo, deviceIndex) in monitoringProductsData[index].devices">
              <v-row>
                <v-col cols="12" md="2">
                  <v-autocomplete
                    :menu-props="{ offsetY: true }"
                    :items="getDevices(deviceInfo.device, product.deviceType)"
                    item-text="campaign.entityName"
                    item-value="id"
                    v-model="deviceInfo.instanceId"
                    :label="`${$t('EditCampaign.devicesAndServices.device')} *`"
                    :title="$t('EditCampaign.devicesAndServices.device')"
                    outlined
                    background-color="#FFF"
                    color="#0098E1"
                    required
                    :rules="[rules.fieldRequired]"
                    hide-details="auto"
                    hide-no-data
                    :disabled="!isStartDateEditable(deviceInfo.startDate)"
                    @change="handleSelectDevice()"
                  />
                </v-col>
                <v-col cols="12" md="2">
                  <v-menu
                    v-model="deviceInfo.startDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :key="`startDate-${index}`"
                        v-model="deviceInfo.startDate"
                        :label="`${$t('EditCampaign.devicesAndServices.startDate')} *`"
                        :title="$t('EditCampaign.devicesAndServices.startDate')"
                        background-color="white"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        persistent-hint
                        flat
                        color="#0098E1"
                        clearable
                        :rules="[rules.fieldRequired, (v) => rules.validateStartDate(v, index, deviceIndex)]"
                        hide-details="auto"
                        :disabled="!isStartDateEditable(deviceInfo.startDate)"
                        append-icon="mdi-calendar"
                        @click:append="deviceInfo.startDateMenu = true"
                      />
                    </template>
                    <v-date-picker
                      v-model="deviceInfo.auxStartDate"
                      @change="handleDeviceDateChange($event, deviceInfo, 'startDate')"
                      color="#0098E1"
                      locale="es-AR"
                      :min="getCurrentDate()"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12" md="2">
                  <v-menu
                    v-model="deviceInfo.endDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :key="`endDate-${index}`"
                        v-model="deviceInfo.endDate"
                        :label="`${$t('EditCampaign.devicesAndServices.endDate')} *`"
                        :title="$t('EditCampaign.devicesAndServices.endDate')"
                        background-color="white"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        persistent-hint
                        flat
                        color="#0098E1"
                        clearable
                        :rules="[rules.fieldRequired, (v) => validateEndDateForDevice(v, deviceInfo, index)]"
                        hide-details="auto"
                        append-icon="mdi-calendar"
                        @click:append="deviceInfo.endDateMenu = true"
                        :disabled="!deviceInfo.startDate || !isEndDateEditable(deviceInfo.endDate) || deviceHasExtension(index, deviceIndex)"
                      />
                    </template>
                    <v-date-picker
                      v-model="deviceInfo.auxEndDate"
                      @change="handleDeviceDateChange($event, deviceInfo, 'endDate')"
                      color="#0098E1"
                      locale="es-AR"
                      :min="getCurrentDate()"
                    />
                  </v-menu>
                </v-col>
                 <v-col cols="12" md="2">
                   <v-autocomplete
                     :menu-props="{ offsetY: true }"
                     :items="fields"
                     item-text="name"
                     item-value="id"
                     v-model="deviceInfo.fieldInstanceId"
                     :label="`${$t('EditCampaign.devicesAndServices.field')} *`"
                     :title="$t('EditCampaign.devicesAndServices.field')"
                     outlined
                     background-color="#FFF"
                     color="#0098E1"
                     required
                     :rules="[rules.fieldRequired]"
                     hide-details="auto"
                     :disabled="!isStartDateEditable(deviceInfo.startDate)"
                   >
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="deviceInfo.coordinates"
                    :label="`${$t('EditCampaign.devicesAndServices.coordinates')} *`"
                    :title="$t('EditCampaign.devicesAndServices.coordinates')"
                    :hint="$t('EditCampaign.devicesAndServices.coordinatesHint')"
                    background-color="white"
                    outlined
                    persistent-hint
                    flat
                    :rules="[rules.fieldRequired, (v) => rules.validateLatLonForEditable(v, deviceInfo.startDate)]"
                    hide-details="auto"
                    :disabled="!isStartDateEditable(deviceInfo.startDate)"
                    validate-on-blur
                  />
                </v-col>
              </v-row>
              <v-divider v-if="deviceIndex < monitoringProductsData[index].devices.length - 1" class="mt-2 mb-6" />
            </div>
          </template>

          <!-- Extensions products -->
          <template v-for="(product, index) in extensionProducts">
            <v-row align="baseline">
              <p class="body-text pointer" @click="toggleProductExpanded('extension', index)"><b>{{product.quantity}}</b> {{getProductDetailText(product)}}</p>
              <v-btn class="ml-2" text fab x-small @click="toggleProductExpanded('extension', index)">
                <v-icon
                  color="black"
                >{{extensionProductsExpanded[index] ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon>
              </v-btn>
            </v-row>
            <div v-show="extensionProductsExpanded[index]">
              <v-row align="baseline">
                <p class="extension-description-text">{{$t('NewCampaignInitialData.devicesAndServices.extensionProductsDescription')}}</p>
              </v-row>
              <div v-for="(extensionInfo, extensionIndex) in extensionProductsData[index].extensions">
                <v-divider v-if="extensionIndex > 0" class="my-2" />
                <v-row>
                  <v-col cols="12" md="2">
                    <v-autocomplete
                      :menu-props="{ offsetY: true }"
                      :items="getExtensionDevices(extensionInfo.device)"
                      item-text="campaign.entityName"
                      item-value="id"
                      v-model="extensionInfo.device"
                      :label="`${$t('NewCampaignInitialData.devicesAndServices.device')} *`"
                      :title="$t('NewCampaignInitialData.devicesAndServices.device')"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      hide-details="auto"
                      :no-data-text="$t('NewCampaignInitialData.devicesAndServices.noDevices')"
                      :rules="[() => validateExtensionDevice(extensionInfo)]"
                      :disabled="extensionInfo.applied"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      :menu-props="{ offsetY: true }"
                      :items="[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]"
                      v-model="extensionInfo.unitsToExtend"
                      :label="`${$t('NewCampaignInitialData.devicesAndServices.unitsToExtend')}`"
                      :title="$t('NewCampaignInitialData.devicesAndServices.unitsToExtend')"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      hide-details="auto"
                      :rules="[() => validateUnitsToExtend(index)]"
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-menu
                      v-model="extensionInfo.endDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :key="`endDate-${extensionIndex}`"
                          v-model="extensionInfo.endDate"
                          :label="`${$t('NewCampaignInitialData.devicesAndServices.endDate')}`"
                          :title="$t('NewCampaignInitialData.devicesAndServices.endDate')"
                          background-color="white"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          persistent-hint
                          flat
                          color="#0098E1"
                          clearable
                          hide-details="auto"
                          append-icon="mdi-calendar"
                          @click:append="extensionInfo.endDateMenu = true"
                          :disabled="!extensionInfo.device || !isEndDateEditable(extensionInfo.endDate)"
                          :rules="[() => validateEndDateForExtension(extensionInfo.endDate, extensionInfo, index)]"
                        />
                      </template>
                      <v-date-picker
                        v-model="extensionInfo.auxEndDate"
                        @change="handleExtensionDateChange($event, extensionInfo, 'endDate')"
                        color="#0098E1"
                        locale="es-AR"
                        :min="getCurrentDate()"
                      />
                    </v-menu>
                  </v-col>
                  <v-col align-self="center" v-if="!extensionInfo.applied">
                    <a @click="removeExtension(index, extensionIndex)" class="remove-extension-btn">{{$t('NewCampaignInitialData.devicesAndServices.removeExtension')}}</a>
                  </v-col>
                </v-row>
              </div>
              <v-row class="mt-1" align="baseline" v-if="hasUnassignedExtensions(index)">
                <a @click="addNewExtension(index)" class="add-extension-btn">{{$t('NewCampaignInitialData.devicesAndServices.addNewExtension')}}</a>
              </v-row>
            </div>
          </template>
        </div>

        <v-row class="mt-2" align="end">
          <v-col align="end">
            <v-btn color="#FFFFF" class="back-button" @click="goBack()">{{$t('EditCampaign.devicesAndServices.back')}}</v-btn>
          </v-col>
          <v-col align="start">
            <v-btn :loading="loading" color="#0098E1" class="submit-button" @click="saveChanges()">{{$t('EditCampaign.devicesAndServices.finish')}}</v-btn>
          </v-col>
        </v-row>
      </v-form>

    </v-container>
  </template>

  <script>
  import { EQUIPMENT_TYPE, CAMPAIGN_VALIDATION, WEBHOOKS_PATHS, PRODUCT_TYPES } from '../../constants/const'
  import { ddMMyyyyToJsDate, validateLatLon } from '../../lib/helpers'
  import _sortBy from 'lodash/sortBy'
  import _get from 'lodash/get'
  import _cloneDeep from 'lodash/cloneDeep'
  import { updatePreDataInformationEditCampaign, updateCampaign } from '../../lib/crudOperations/write'
  import { auth } from '../../plugins/firebase'
  import { trackEvent } from '../../lib/track-events'
  import { EVENTS_TO_TRACK } from '../../constants/const'
  export default {
    name: 'EditCampaignDevicesAndServices',
    data () {
      return {
        campaignId: this.$route.params.campaignId,
        products: [],
        monitoringProductsExpanded: [],
        extensionProductsExpanded: [],
        monitoringProductsData: [],
        extensionProductsData: [],
        oldMonitoringProductsData: [],
        oldExtensionProductsData: [],
        isLoading: true,
        isEditingCampaignName: false,
        editedCampaignName: '',
        rules: {
          fieldRequired: v => !!v || this.$t('Config.fieldIsRequired'),
          fieldRequiredForEditable: (v, startDate) => {
            if (!this.isStartDateEditable(startDate)) return true
            return !!v || this.$t('Config.fieldIsRequired')
          },
          arrayFieldRequired: v => !!v && !!v.length || this.$t('Config.fieldIsRequired'),
          validateLatLon: v => validateLatLon(v) || this.$t('Config.allCoordinatesRule'),
          validateStartDate: (startDate, productIndex, deviceIndex) => {
            if (!startDate) return true // If no date, don't validate here (already validated with fieldRequired)

            const isOriginalDate = this.isOriginalDate(startDate, productIndex, deviceIndex)
            if (isOriginalDate) {
              return true
            }

            // Skip validation for non-editable fields (past dates)
            if (!this.isStartDateEditable(startDate)) return true

            const [day, month, year] = startDate.split('/')
            const selectedDate = new Date(year, month - 1, day)
            const minDate = new Date()
            minDate.setDate(minDate.getDate() + CAMPAIGN_VALIDATION.MIN_DAYS_BEFORE_START)
            return selectedDate >= minDate || this.$t('Config.startDateMinDaysRule', {minDays: CAMPAIGN_VALIDATION.MIN_DAYS_BEFORE_START})
          },
          validateEndDate: (endDate) => {
            if (!endDate) return true // If no date, don't validate here (already validated with fieldRequired)
            return true // This validation will be done in the specific method
          },
          validateLatLonForEditable: (coordinates, startDate) => {
            if (!coordinates) return true
            // Skip validation for non-editable fields (past dates)
            if (!this.isStartDateEditable(startDate)) return true
            return validateLatLon(coordinates) || this.$t('Config.allCoordinatesRule')
          }
        },
        isFormValid: false,
        loading: false,
        snackbar: {
          show: false,
          timeout: 3000,
          color: 'error',
          text: ''
        },
        newDevicesItemsList: {},
        loadingCampaignName: false,
        originalDatesByDevice: {}
      }
    },
    async created () {
      await this.loadCampaignData()

      this.isLoading = false
    },
    methods: {
      async loadCampaignData () {
        try {
          this.loading = true

          // Get campaign using store getter
          const campaign = this.$store.getters.getCampaign(this.campaignId)
          if (!campaign) {
            this.snackbar = {
              show: true,
              timeout: 3000,
              color: 'error',
              text: this.$t('UI.campaignNotFound')
            }
            this.$router.push({name: 'OrganizationHome'})
            return
          }

          // Process products from preDataProductsDetail
          if (campaign.preDataProductsDetail && Array.isArray(campaign.preDataProductsDetail)) {
            this.products = campaign.preDataProductsDetail.map(product => {
              let productData
              if (product.type === PRODUCT_TYPES.MONITORING) {
                productData = {
                  quantity: product.devices ? product.devices.length : 1,
                  deviceType: product.deviceType || EQUIPMENT_TYPE.PIVOT,
                  durationMonths: product.durationMonths || 12,
                  serviceName: product.serviceName || 'SERVICE',
                  id: product.id,
                  devices: product.devices || [],
                  type: product.type
                }
              } else if (product.type === PRODUCT_TYPES.EXTENSION) {
                productData = {
                  durationMonths: product.durationMonths || 1,
                  id: product.id,
                  type: product.type,
                  quantity: product.quantity,
                  extensions: product.extensions || []
                }
              }
              return productData
            })
          } else if (campaign.product && Array.isArray(campaign.product)) {
            // Fallback for old structure
            this.products = campaign.product
          } else {
            // If no products, show error message
            this.snackbar = {
              show: true,
              timeout: 3000,
              color: 'error',
              text: this.$t('UI.campaignNotFound')
            }
            this.$router.push({name: 'OrganizationHome'})
          }

          // Initialize products data after loading
          if (this.products.length > 0) {
            this.initializeProductsData()
          }

        } catch (error) {
          console.error('Error loading campaign data:', error)
          this.snackbar = {
            show: true,
            timeout: 3000,
            color: 'error',
            text: this.$t('UI.serverError')
          }
          this.$router.push({name: 'OrganizationHome'})
        } finally {
          this.loading = false
        }
      },
      initializeProductsData () {
        this.monitoringProductsExpanded = this.products.filter(product => product.type === PRODUCT_TYPES.MONITORING).map(() => false)
        this.originalDatesByDevice = {}

        this.monitoringProductsData = this.products.filter(product => product.type === PRODUCT_TYPES.MONITORING).map((product, productIndex) => {
          const productDevices = []
          const existingDevices = product.devices || []

          for (let i = 0; i < product.quantity; i++) {
            const existingDevice = existingDevices[i] || {}
            const deviceData = {
              device: _get(existingDevice, 'device', null),
              startDate: _get(existingDevice, 'startDate', null),
              endDate: _get(existingDevice, 'endDate', null),
              field: _get(existingDevice, 'field', null),
              coordinates: _get(existingDevice, 'coordinates', ''),
              isNewField: _get(existingDevice, 'isNewField', false),
              instanceId: _get(existingDevice, 'instanceId', null),
              fieldInstanceId: _get(existingDevice, 'fieldInstanceId', null),
              startDateMenu: false,
              auxStartDate: null,
              endDateMenu: false,
              auxEndDate: null,
              productIndex: productIndex,
              deviceIndex: i
            }

            // Store original start date for validation comparison
            if (deviceData.startDate) {
              // Store original date by device identifier
              const deviceKey = `${productIndex}-${i}`
              this.originalDatesByDevice[deviceKey] = deviceData.startDate
            }

            productDevices.push(deviceData)
          }
          return {
            ...product,
            devices: productDevices
          }
        })
        this.oldMonitoringProductsData = _cloneDeep(this.monitoringProductsData)

        this.extensionProductsData = this.extensionProducts.map((product) => {
          const productExtensions = []
          if (!product.extensions || product.extensions.length === 0) {
            productExtensions.push({
              device: null,
              endDateMenu: false,
              auxEndDate: null,
              endDate: null,
              unitsToExtend: 1,
              applied: false
            })
          } else {
            productExtensions.push(...product.extensions)
          }
          return {
            ...product,
            extensions: productExtensions
          }
        })
        this.oldExtensionProductsData = _cloneDeep(this.extensionProductsData)
      },
      handleSelectDevice () {
        this.checkExtensionsDevices()
      },
      goBack () {
        trackEvent(EVENTS_TO_TRACK.CLICK_EDIT_CAMPAIGN_BACK_BUTTON, {
          campaignId: this.campaignId
        })
        this.$router.push({name: 'OrganizationHome'})
      },
      getProductDetailText (product) {
        if (product.type === PRODUCT_TYPES.MONITORING) {
          let deviceType = ''
          switch (product.deviceType) {
            case EQUIPMENT_TYPE.PIVOT:
              if (product.quantity > 1) {
                deviceType = this.$t('EditCampaign.pivots')
              } else {
                deviceType = this.$t('EditCampaign.pivot')
              }
              break
            case EQUIPMENT_TYPE.AF:
              if (product.quantity > 1) {
                deviceType = this.$t('EditCampaign.linears')
              } else {
                deviceType = this.$t('EditCampaign.linear')
              }
              break
            case EQUIPMENT_TYPE.IR:
              if (product.quantity > 1) {
                deviceType = this.$t('EditCampaign.irrigationReels')
              } else {
                deviceType = this.$t('EditCampaign.irrigationReel')
              }
              break
          }

          return this.$t('EditCampaign.introduction.contractedEquipmentsDetail', {deviceType, serviceName: product.serviceName.toUpperCase(), durationMonths: product.durationMonths})
        } else if (product.type === PRODUCT_TYPES.EXTENSION) {
          let durationMonths = product.durationMonths > 1 ? `${product.durationMonths} ${this.$t('EditCampaign.introduction.months')}` : `${product.durationMonths} ${this.$t('EditCampaign.introduction.month')}`
          if (product.quantity > 1) {
            return this.$t('EditCampaign.introduction.contractedExtensionsDetail', {durationMonths})
          } else {
            return this.$t('EditCampaign.introduction.contractedExtensionDetail', {durationMonths})
          }
        }
      },
      toggleProductExpanded (type, index) {
        if (type === PRODUCT_TYPES.MONITORING) {
          this.$set(this.monitoringProductsExpanded, index, !this.monitoringProductsExpanded[index])
        } else {
          this.$set(this.extensionProductsExpanded, index, !this.extensionProductsExpanded[index])
        }
      },
      handleDeviceDateChange (val, device, field) {
        this.formatDeviceDate(val, device, field)
      },
      formatDeviceDate (val, device, field) {
        const [year, month, day] = val.split('-')
        const formattedDate = `${day}/${month}/${year}`
        device[field] = formattedDate
        device[`${field}Menu`] = false
        this.$nextTick(() => {
          if (this.$refs.devicesForm) {
            this.$refs.devicesForm.validate()
          }
        })
      },
      handleExtensionDateChange (val, extensionInfo, field) {
        this.formatExtensionDate(val, extensionInfo, field)

        // Update end date in monitoring products data
        const productIndex = this.monitoringProductsData.findIndex(product => product.devices.findIndex(device => device.instanceId === extensionInfo.device) !== -1)
        const deviceIndex = this.monitoringProductsData[productIndex].devices.findIndex(device => device.instanceId === extensionInfo.device)
        this.monitoringProductsData[productIndex].devices[deviceIndex][field] = extensionInfo[field]
      },
      formatExtensionDate (val, extensionInfo, field) {
        const [year, month, day] = val.split('-')
        const formattedDate = `${day}/${month}/${year}`
        extensionInfo[field] = formattedDate
        extensionInfo[`${field}Menu`] = false
        this.$nextTick(() => {
          if (this.$refs.devicesForm) {
            this.$refs.devicesForm.validate()
          }
        })
      },
      validateEndDateForDevice (endDate, deviceInfo, productIndex) {
        if (!endDate || !deviceInfo.startDate) return true
        const [startDay, startMonth, startYear] = deviceInfo.startDate.split('/')
        const [endDay, endMonth, endYear] = endDate.split('/')
        const startDate = new Date(startYear, startMonth - 1, startDay)
        const endDateObj = new Date(endYear, endMonth - 1, endDay)

        // Validate that end date is after start date
        if (endDateObj <= startDate) {
          return this.$t('Config.endDateAfterStartDateRule')
        }

        // Get service duration in months
        const product = this.products[productIndex]

        // Get extensions assigned to the device
        const extensions = this.extensionProductsData.flatMap(product => product.extensions).filter(extension => extension.device === deviceInfo.instanceId)
        const extensionsMonths = extensions.reduce((acc, extension) => acc + extension.unitsToExtend * this.extensionProductsData.find(product => product.extensions.find(extension => extension.device === deviceInfo.instanceId)).durationMonths, 0)

        // Get max end date
        const maxEndDate = new Date(startDate)
        maxEndDate.setMonth(maxEndDate.getMonth() + product.durationMonths + extensionsMonths)
        return endDateObj <= maxEndDate || this.$t('Config.endDateMaxDurationRule')
      },
      validateEndDateForExtension (endDate, extensionInfo, productIndex) {
        if (!endDate && !extensionInfo.device) {
          return true
        } else if (!endDate && extensionInfo.device) {
          return this.$t('Config.fieldIsRequired')
        }
        const [startDay, startMonth, startYear] = this.monitoringProductsData.find(product => product.devices.find(device => device.instanceId === extensionInfo.device)).devices.find(device => device.instanceId === extensionInfo.device).startDate.split('/')
        const [endDay, endMonth, endYear] = endDate.split('/')
        const startDate = new Date(startYear, startMonth - 1, startDay)
        const endDateObj = new Date(endYear, endMonth - 1, endDay)

        // Validate that end date is after start date
        if (endDateObj <= startDate) {
          return this.$t('Config.endDateAfterStartDateRule')
        }

        // Get service duration in months
        const product = this.monitoringProductsData.find(product => product.devices.find(device => device.instanceId === extensionInfo.device))
        const productBaseDurationMonths = product.durationMonths
        const productExtensionsMonths = extensionInfo.unitsToExtend * this.extensionProductsData[productIndex].durationMonths
        const maxEndDate = new Date(startDate)
        maxEndDate.setMonth(maxEndDate.getMonth() + productBaseDurationMonths + productExtensionsMonths)

        // Validate that end date is before the max end date
        return endDateObj <= maxEndDate || this.$t('Config.endDateMaxDurationRule')
      },
      validateExtensionDevice (extensionInfo) {
        if (!extensionInfo.device && extensionInfo.endDate) {
          return this.$t('Config.fieldIsRequired')
        }
        if (extensionInfo.device) {
          const deviceProduct = this.monitoringProductsData.find(product => product.devices.find(device => device.instanceId === extensionInfo.device))
          const deviceServiceData = deviceProduct.devices.find(device => device.instanceId === extensionInfo.device)
          const deviceServiceEndDate = deviceServiceData.endDate

          if (!extensionInfo.applied && ddMMyyyyToJsDate(deviceServiceEndDate) < new Date()) {
            return this.$t('Config.extensionToServiceEndedDevice')
          }
        }

        return true
      },
      getCurrentDate () {
        const today = new Date()
        const year = today.getFullYear()
        const month = String(today.getMonth() + 1).padStart(2, '0')
        const day = String(today.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      },
      isStartDateEditable (startDate) {
        if (!startDate) return true // If no start date, allow editing

        // Parse start date (DD/MM/YYYY format)
        const [day, month, year] = startDate.split('/')
        const startDateObj = new Date(year, month - 1, day)

        // Get current date (without hours to compare only dates)
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        // Start date is editable if it's greater than today (future)
        return startDateObj >= today
      },
      isEndDateEditable (endDate) {
        if (!endDate) return true // If no end date, allow editing

        // Parse end date (DD/MM/YYYY format)
        const [day, month, year] = endDate.split('/')
        const endDateObj = new Date(year, month - 1, day)

        // Get current date (without hours to compare only dates)
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        // End date is editable if it's greater than today (future)
        return endDateObj >= today
      },
      async saveChanges () {
        trackEvent(EVENTS_TO_TRACK.CLICK_EDIT_CAMPAIGN_SAVE_CHANGES_BUTTON, {
          campaignId: this.campaignId
        })
        if (this.$refs.devicesForm.validate()) {
          try {
            this.loading = true
            await updatePreDataInformationEditCampaign(
              this.campaignId,
              [...this.oldMonitoringProductsData, ...this.oldExtensionProductsData],
              [...this.monitoringProductsData, ...this.extensionProductsData]
            )

            // Load campaign data querying the webhook
            const webhookUrl = `${process.env.WEBHOOKS_URL}${WEBHOOKS_PATHS.LOAD_CAMPAIGN_DATA}`
            const webhookData = {
              campaignId: this.campaignId,
              isNewCampaign: false
            }
            await fetch(webhookUrl, {
              method: 'POST',
              body: JSON.stringify(webhookData),
              headers: {
                authorization: process.env.WEBHOOKS_BASIC_TOKEN,
                'Content-Type': 'application/json',
                'auth-token': await auth.currentUser.getIdToken()
              }
            })

            this.snackbar = {
              show: true,
              timeout: 3000,
              color: 'success',
              text: this.$t('EditCampaign.changesSaved')
            }

            this.$router.push({name: 'OrganizationHome'}) // Redirect to organization home after saving
          } catch (e) {
            console.log(e)
            this.snackbar = {
              show: true,
              timeout: 3000,
              color: 'error',
              text: this.$t('EditCampaign.errorSavingChanges')
            }
          } finally {
            this.loading = false
          }
        }
      },
      getDevices (deviceId, deviceType) {
        const devices = this.$store.getters.organizationDevices

        // Get all selected device IDs
        const selectedDeviceIds = []
        this.monitoringProductsData.forEach(product => {
          product.devices.forEach(deviceInfo => {
            if (deviceInfo.device && deviceInfo.device !== deviceId) {
              selectedDeviceIds.push(deviceInfo.device)
            }
          })
        })

        // Get all cloned device IDs from all devices
        const clonedDeviceIds = []
        devices.forEach(device => {
          if (device.clonedFrom && Array.isArray(device.clonedFrom)) {
            clonedDeviceIds.push(...device.clonedFrom)
          }
        })

        // Filter devices that are not selected, not cloned, and match device type
        const availableDevices = devices.filter(device => {
          const isNotSelected = !selectedDeviceIds.includes(device.id)
          const isNotCloned = !clonedDeviceIds.includes(device.id)
          const matchesType = device.equipment.type === deviceType

          return isNotSelected && isNotCloned && matchesType
        })

        return _sortBy(availableDevices, device => device.campaign.entityName.toLowerCase())
      },
      formatCampaignDate (startDate, endDate) {
        if (!startDate) return ''

        const formatDate = (date) => {
          if (date && date.toDate) {
            return date.toDate().toLocaleDateString('es-AR', { month: 'short', year: 'numeric' })
          } else if (date) {
            return new Date(date).toLocaleDateString('es-AR', { month: 'short', year: 'numeric' })
          }
          return ''
        }

        const start = formatDate(startDate)
        const end = formatDate(endDate)

        if (start && end) {
          return `${start} / ${end}`
        } else if (start) {
          return start
        }
        return ''
      },
      editCampaign () {
        this.$router.push({name: 'EditCampaignInitialData', params: {campaignId: this.campaignId}})
      },
      startEditingCampaignName () {
        this.isEditingCampaignName = true
        this.editedCampaignName = this.selectedCampaign.name
        this.$nextTick(() => {
          this.$refs.campaignNameInput.focus()
        })
      },
      async saveCampaignName () {
        if (this.editedCampaignName.trim() === '') {
          this.editedCampaignName = this.selectedCampaign.name
          this.isEditingCampaignName = false
          return
        }

        try {
          this.loadingCampaignName = true
          await updateCampaign(this.campaignId, { name: this.editedCampaignName.trim() })
          this.selectedCampaign.name = this.editedCampaignName.trim()
          this.isEditingCampaignName = false
          this.snackbar = {
            show: true,
            timeout: 3000,
            color: 'success',
            text: this.$t('EditCampaign.campaignNameSaved')
          }
        } catch (error) {
          console.error('Error saving campaign name:', error)
          this.editedCampaignName = this.selectedCampaign.name
          this.isEditingCampaignName = false
          this.snackbar = {
            show: true,
            timeout: 3000,
            color: 'error',
            text: this.$t('EditCampaign.errorSavingChanges')
          }
        } finally {
          this.loadingCampaignName = false
        }
      },
      cancelEditingCampaignName () {
        this.editedCampaignName = this.selectedCampaign.name
        this.isEditingCampaignName = false
      },
      handleBlur () {
        this.saveCampaignName()
      },
      isOriginalDate (startDate, productIndex, deviceIndex) {
        if (!startDate) return false

        const deviceKey = `${productIndex}-${deviceIndex}`

        const originalDateForDevice = this.originalDatesByDevice[deviceKey]
        return originalDateForDevice === startDate
      },
      getExtensionDevices (deviceId) {
        // Extract all device IDs that are already selected in extensions (excluding current device)
        const selectedDevicesIds = this.extensionProductsData
          .flatMap(product => product.extensions)
          .filter(extension => extension.device && extension.device !== deviceId)
          .map(extension => extension.device)

        // Get all available devices from monitoring products
        const availableDevices = this.monitoringProductsData
          .flatMap((product) =>
            product.devices.map((device) => {
              // Try to find existing device in organization, fallback to new device template
              const existingDevice = this.$store.getters.organizationDevices.find(d => d.id === device.instanceId)

              if (existingDevice) {
                return existingDevice
              }
            })
          )
          .filter(device => !selectedDevicesIds.includes(device.id))

        // Return devices sorted by entity name (case-insensitive)
        return _sortBy(availableDevices, device => device.campaign.entityName.toLowerCase())
      },
      deviceHasExtension (productIndex, deviceIndex) {
        return this.extensionProductsData.flatMap(product => product.extensions).some(extension => extension.device === this.monitoringProductsData[productIndex].devices[deviceIndex].instanceId)
      },
      validateUnitsToExtend (productIndex) {
        const sumOfUnitsToExtend = this.extensionProductsData.flatMap(product => product.extensions).reduce((acc, extension) => acc + extension.unitsToExtend, 0)
        return sumOfUnitsToExtend <= this.extensionProducts[productIndex].quantity || this.$t('Config.unitsToExtendMaxRule')
      },
      hasUnassignedExtensions (index) {
        return this.extensionProductsData[index].extensions.reduce((acc, extension) => acc + extension.unitsToExtend, 0) < this.extensionProducts[index].quantity
      },
      addNewExtension (index) {
        trackEvent(EVENTS_TO_TRACK.CLICK_CAMPAIGN_ONBOARDING_ADD_NEW_EXTENSION_BUTTON, {
          campaignId: this.campaignId
        })
        this.extensionProductsData[index].extensions.push({
          device: null,
          unitsToExtend: 1,
          endDateMenu: false,
          auxEndDate: null,
          endDate: null,
          applied: false
        })
      },
      removeExtension (index, extensionIndex) {
        trackEvent(EVENTS_TO_TRACK.CLICK_CAMPAIGN_ONBOARDING_REMOVE_EXTENSION_BUTTON, {
          campaignId: this.campaignId
        })
        if (this.extensionProductsData[index].extensions.length === 1) {
          this.extensionProductsData[index].extensions[0].device = null
          this.extensionProductsData[index].extensions[0].endDate = null
          this.extensionProductsData[index].extensions[0].auxEndDate = null
        } else {
          this.extensionProductsData[index].extensions.splice(extensionIndex, 1)
        }
      },
      checkExtensionsDevices () {
        const availableDevices = this.getExtensionDevices()
        this.extensionProductsData.forEach(product => {
          product.extensions.forEach(extension => {
            if (extension.device && !availableDevices.some(device => device.id === extension.device)) {
              extension.device = null
              extension.endDate = null
              extension.auxEndDate = null
            }
          })
        })
      }
    },
    computed: {
      fields () {
        const fields = this.$store.getters.getCampaignFields(this.campaignId)

        // Get all cloned field IDs from all fields
        const clonedFieldIds = []
        fields.forEach(field => {
          if (field.clonedFrom && Array.isArray(field.clonedFrom)) {
            clonedFieldIds.push(...field.clonedFrom)
          }
        })

        // Filter fields that are not cloned and return sorted
        const availableFields = fields.filter(field =>
          !clonedFieldIds.includes(field.id)
        )

        return _sortBy(availableFields, field => field.name.toLowerCase())
      },
      organization () {
        return this.$store.getters.userOrganization.name
      },
      selectedCampaign () {
        const user = this.$store.getters.user
        return user.campaigns.find(c => c.id === this.campaignId)
      },
      monitoringProducts () {
        return this.products.filter(product => product.type === PRODUCT_TYPES.MONITORING)
      },
      extensionProducts () {
        return this.products.filter(product => product.type === PRODUCT_TYPES.EXTENSION)
      }
    }
  }
  </script>




// WEBPACK FOOTER //
// EditCampaignDevicesAndServices.vue?bffbd2f8